import { Link, createFileRoute } from "@tanstack/react-router";
import { XIcon } from "lucide-react";

import { DebouncedInput } from "~/components/debounced-input";
import { Field, FilterSelect } from "~/components/filter-controls";
import { FilterChips } from "~/components/filter-chips";
import { PageHeader } from "~/components/page-header";
import { PaginationBar } from "~/components/pagination-bar";
import { StatusBadge } from "~/components/status-badge";
import { TableEmpty, TableError, TableSkeleton } from "~/components/table-states";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { errorMessage } from "~/lib/api";
import { BRIDGE_ORDER_STATUSES } from "~/lib/enums";
import { formatDateTime, formatIDR, orDash } from "~/lib/format";
import { useBridgeOrders } from "~/lib/queries";
import {
  hasActiveFilters,
  toFilters,
  validateBridgeSearch,
  type BridgeSearch,
} from "./-search";

export const Route = createFileRoute("/_authed/bridge/")({
  validateSearch: validateBridgeSearch,
  component: BridgeOrdersPage,
});

const COLUMNS = 5;

/**
 * Shopify orders that became Altura payments.
 *
 * The bridge is how Arutala actually works, and until now none of its three
 * tables had a screen. Read-only: bridge_orders is written by the Shopify
 * webhook handlers and the bridge service, and the admin surface observes it.
 *
 * No default status filter. Unlike webhook events and callbacks, there is no one
 * state that is "why anyone opens this" — someone arrives here with a customer's
 * email or a merchant's order number, not looking for a failure class.
 */
function BridgeOrdersPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const filters = toFilters(search);
  const { data, error, isPending, isFetching, refetch } = useBridgeOrders(filters);

  function patch(next: Partial<BridgeSearch>) {
    void navigate({
      search: (previous) => ({
        ...previous,
        ...next,
        offset: "offset" in next ? next.offset : 0,
      }),
      replace: true,
    });
  }

  function clearFilters() {
    void navigate({ search: (previous) => ({ limit: previous.limit }), replace: true });
  }

  const rows = data?.items ?? [];
  const filtered = hasActiveFilters(search);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Bridge"
        description="Shopify orders, the payment links created for them, and the Altura transactions behind those."
      />

      <FilterBar
        search={search}
        onChange={patch}
        onClear={clearFilters}
        hasFilters={filtered}
      />

      <FilterChips
        value={search.status}
        options={BRIDGE_ORDER_STATUSES}
        onChange={(next) => patch({ status: next })}
      />

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Order</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Gross</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {isPending ? (
              <TableSkeleton columns={COLUMNS} />
            ) : error ? (
              <TableError
                columns={COLUMNS}
                message={errorMessage(error)}
                onRetry={() => void refetch()}
              />
            ) : rows.length === 0 ? (
              <TableEmpty
                columns={COLUMNS}
                message={
                  filtered
                    ? "No bridge orders match these filters."
                    : "No Shopify orders have come through the bridge yet."
                }
                onClear={filtered ? clearFilters : undefined}
              />
            ) : (
              rows.map((row) => (
                <TableRow key={row.shopify_order_id}>
                  <TableCell>
                    <Link
                      to="/bridge/$shopifyOrderId"
                      params={{ shopifyOrderId: row.shopify_order_id }}
                      className="text-primary hover:underline"
                    >
                      {/* The merchant-facing name, falling back to the id. A
                          support conversation quotes "#1042", not the numeric
                          Shopify order id. */}
                      {row.shopify_order_name || row.shopify_order_id}
                    </Link>
                  </TableCell>

                  <TableCell className="max-w-[16rem] truncate text-muted-foreground">
                    {orDash(row.customer_email)}
                  </TableCell>

                  <TableCell>
                    <StatusBadge status={row.status} />
                  </TableCell>

                  {/* Already a decimal string: the server converted from the
                      bigint minor units this table stores. */}
                  <TableCell className="text-right tabular-nums">
                    {formatIDR(row.amount_gross)}
                  </TableCell>

                  <TableCell className="text-muted-foreground">
                    {formatDateTime(row.created_at)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {data ? (
          <PaginationBar
            pagination={data.pagination}
            onChange={(next) => patch(next)}
            disabled={isFetching}
          />
        ) : null}
      </div>
    </div>
  );
}

function FilterBar({
  search,
  onChange,
  onClear,
  hasFilters,
}: {
  search: BridgeSearch;
  onChange: (patch: Partial<BridgeSearch>) => void;
  onClear: () => void;
  hasFilters: boolean;
}) {
  return (
    <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3">
      {/* Exact match, and the placeholder says so. The column is not normalised
          — the bridge stores whatever Shopify sent — so a partial or
          differently-cased address will not match, and pretending otherwise
          would return an empty table that reads as "this customer has no
          orders". */}
      <Field label="Customer email">
        <DebouncedInput
          value={search.customer_email ?? ""}
          onCommit={(value) => onChange({ customer_email: value || undefined })}
          placeholder="Exact address"
          className="w-56"
        />
      </Field>

      <Field label="Order name">
        <DebouncedInput
          value={search.shopify_order_name ?? ""}
          onCommit={(value) => onChange({ shopify_order_name: value || undefined })}
          placeholder="Starts with…"
          className="w-36"
        />
      </Field>

      <Field label="Shopify customer ID">
        <DebouncedInput
          value={search.shopify_customer_id ?? ""}
          onCommit={(value) => onChange({ shopify_customer_id: value || undefined })}
          placeholder="Exact ID"
          className="w-44"
        />
      </Field>

      <Field label="Status">
        <FilterSelect
          value={search.status ?? null}
          onValueChange={(value) => onChange({ status: value ?? undefined })}
          placeholder="Any status"
          className="w-48"
          options={BRIDGE_ORDER_STATUSES.map((status) => ({ value: status, label: status }))}
        />
      </Field>

      <Field label="From">
        <Input
          type="date"
          value={search.start_date ?? ""}
          onChange={(event) => onChange({ start_date: event.target.value || undefined })}
          className="w-[8.5rem]"
        />
      </Field>

      <Field label="To">
        <Input
          type="date"
          value={search.end_date ?? ""}
          onChange={(event) => onChange({ end_date: event.target.value || undefined })}
          className="w-[8.5rem]"
        />
      </Field>

      {hasFilters ? (
        <Button variant="ghost" size="sm" onClick={onClear} className="ml-auto">
          <XIcon className="size-3.5" />
          Clear
        </Button>
      ) : null}
    </div>
  );
}
